<template>
  <div class="data-page">
    <div class="data-container">
      <section class="hero data-panel">
        <h1>Our Data Collection and Analysis Process</h1>
        <p>
          A quick guide to how Paulie works, what the data means, and how to get more from the
          product.
        </p>
      </section>

      <section class="section-card data-panel accent-sky">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M9 4.5a3 3 0 0 0-3 3V8a3 3 0 0 0-2.5 3v.5A3.5 3.5 0 0 0 7 15h.5v.5A3.5 3.5 0 0 0 11 19h1m1-14.5a3 3 0 0 1 3 3V8a3 3 0 0 1 2.5 3v.5A3.5 3.5 0 0 1 17 15h-.5v.5A3.5 3.5 0 0 1 13 19h-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 5v14m-3-8h6m-5 4h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
          <h2>How It Works</h2>
        </div>
        <div class="chip-row">
          <span class="chip">Collect</span>
          <span class="chip">Map</span>
          <span class="chip">Respond</span>
        </div>
        <div class="copy-block">
          <p>
            Paulie mass-collects and organizes sports betting data from all over the internet,
            including APIs, Twitter, YouTube, prediction markets, sportsbooks (online U.S.
            regulated, casino, and offshore) themselves, and other related sites our deep research
            engine flags.
          </p>
          <p>
            We use <strong>LLM tools</strong> and a <strong>graph structure</strong> to help
            Paulie Chat work with our <strong>database</strong> while still searching the internet
            itself. This helps chat respond faster and with more data at hand than other frontier
            chat-based products.
          </p>
        </div>
      </section>

      <section class="section-card data-panel accent-fuchsia">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M9 4H6a2 2 0 0 0-2 2v3h2.5a1.5 1.5 0 1 1 0 3H4v6a2 2 0 0 0 2 2h6v-2.5a1.5 1.5 0 1 1 3 0V20h3a2 2 0 0 0 2-2v-6h-2.5a1.5 1.5 0 1 1 0-3H20V6a2 2 0 0 0-2-2h-3v2.5a1.5 1.5 0 1 1-3 0V4H9Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            </svg>
          </span>
          <h2>Schema</h2>
        </div>
        <p class="section-intro">
          The scraping engine adheres to a schema comprised of Sportsbooks, Leagues, Teams, Games,
          Odds, Sentiment and Volume, and Scores.
        </p>
        <div class="schema-chip-grid">
          <span class="chip">Sportsbooks</span>
          <span class="chip">Leagues</span>
          <span class="chip">Teams</span>
          <span class="chip">Games</span>
          <span class="chip">Odds</span>
          <span class="chip">Sentiment and Volume</span>
          <span class="chip">Scores</span>
        </div>

        <article class="sub-card data-panel">
          <h3>Sportsbook Classification</h3>
          <div class="bucket-grid">
            <div class="bucket bucket-blue">
              <h4>Prediction Markets</h4>
              <p>
                Markets that function through prediction-market style pricing and participant flow.
              </p>
            </div>
            <div class="bucket bucket-amber">
              <h4>Retail (U.S. Regulated) and Offshore Books</h4>
              <p>
                Consumer-facing books grouped together when their openers and movement read more
                like retail action than market-making action.
              </p>
            </div>
            <div class="bucket bucket-fuchsia">
              <h4>Market-Making Books</h4>
              <p>
                Sharper books that are interpreted as leading price discovery or anchoring
                subsequent market movement.
              </p>
            </div>
          </div>
        </article>

        <div class="callout-grid">
          <div class="callout callout-blue">
            <h4>Liability Lens</h4>
            <p>
              Liability information provides only a crude insight into sportsbook performance,
              periodically supplemented with financial statements and relevant snippets from
              articles or video transcripts.
            </p>
          </div>
          <div class="callout callout-amber">
            <h4>Sentiment and Volume</h4>
            <p>
              Sentiment and volume signals help inform analysis and popularity estimates for
              certain games and markets.
            </p>
          </div>
        </div>
      </section>

      <section class="section-card data-panel accent-green">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 4.5h6.5a2.5 2.5 0 0 1 2.5 2.5V19H7.5A2.5 2.5 0 0 0 5 21V4.5Zm14 0h-6.5A2.5 2.5 0 0 0 10 7v12h6.5A2.5 2.5 0 0 1 19 21V4.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            </svg>
          </span>
          <h2>Historical Record</h2>
        </div>
        <div class="copy-block">
          <p>
            A historical record is kept across the schema so Sportsbooks, Odds, Sentiment and
            Volume, Scores, and the game layer around them can be revisited over time instead of
            treated as one-off snapshots.
          </p>
          <p>
            That history supports deeper trend analysis by combining approximated sportsbook
            performance with traditional team and category performance straight up, ATS, and with
            respect to totals markets.
          </p>
        </div>
        <div class="trend-table-wrap">
          <table class="trend-table">
            <thead>
              <tr>
                <th scope="col">Trend category</th>
                <th scope="col">What we track</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Underdogs</th>
                <td>Home underdogs and away underdogs.</td>
              </tr>
              <tr>
                <th scope="row">Favorites</th>
                <td>Home favorites and away favorites.</td>
              </tr>
              <tr>
                <th scope="row">Team ROI</th>
                <td>Team-level return trends across tracked betting markets.</td>
              </tr>
              <tr>
                <th scope="row">Over / Under ROI</th>
                <td>Totals-market return trends by team.</td>
              </tr>
              <tr>
                <th scope="row">Proprietary Angles</th>
                <td>Dozens of other themes, signals, and market contexts.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="section-card data-panel accent-neutral">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="m12 3 9 16H3L12 3Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M12 9v4m0 3h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </span>
          <h2>Alert Types</h2>
        </div>
        <p class="section-subtitle">
          How retail book liabilities / prediction markets, market-making book liabilities, and hot
          markets show up in Paulie.
        </p>
        <div class="intro-box">
          <p>
            All In and Joes vs Pros both track how retail books / prediction markets and
            market-making books are building liability on a market. The key difference is whether
            those liabilities cluster on the same side or split across opposite sides. Large Move is
            different. It flags when a market suddenly gets hot.
          </p>
        </div>
        <div class="alert-grid">
          <article class="alert-card data-panel alert-green">
            <header>
              <span class="alert-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M8 11 5 8.5a2 2 0 0 0-2.8.2L1 10l4.5 4.5a2 2 0 0 0 2.8 0l1.2-1.2a1.5 1.5 0 0 1 2.1 0l1.2 1.2a2 2 0 0 0 2.8 0L20 10l-1.2-1.3a2 2 0 0 0-2.8-.2L13 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9.5 9.5 12 12l2.5-2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3>All In</h3>
            </header>
            <p>
              Retail books / prediction markets and market-making books are building liability on
              the same side.
            </p>
            <footer>
              Signal: retail book liabilities / prediction markets and market-making book liabilities
              are clustering together.
            </footer>
          </article>
          <article class="alert-card data-panel alert-amber">
            <header>
              <span class="alert-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8h13m0 0-3-3m3 3-3 3M20 16H7m0 0 3-3m-3 3 3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3>Joes vs. Pros</h3>
            </header>
            <p>
              Retail books / prediction markets and market-making books are building liability on
              opposite sides.
            </p>
            <footer>
              Signal: retail book liabilities / prediction markets and market-making book liabilities
              disagree.
            </footer>
          </article>
          <article class="alert-card data-panel alert-red">
            <header>
              <span class="alert-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M7 16.5a5 5 0 1 0 10 0c0-3-2.4-4.6-3.3-6.8-.4 1.1-1.2 2.1-2.2 2.8-.2-1.5-.9-3.1-2.5-4.5C8.3 9.3 7 11.6 7 13.8v2.7Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                  <path d="M12 13.5c-1.2 1-2 2-2 3.3a2 2 0 1 0 4 0c0-1-.5-1.9-2-3.3Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3>Large Move</h3>
            </header>
            <p>
              A market heats up after a fast jump in one tracked bucket or the line itself.
            </p>
            <footer>
              Signal: retail book / prediction market liabilities or market-making book liabilities
              just moved fast.
            </footer>
          </article>
          <article class="alert-card data-panel alert-teal">
            <header>
              <span class="alert-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 16h4l3-8 3 8h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M16 7h4v4M20 7l-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 17H4v-4M4 17l5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3>Reverse Line Movement</h3>
            </header>
            <p>
              Prices and lines on sportsbooks are moving in the opposite direction than the
              majority of bets coming in.
            </p>
          </article>
          <article class="alert-card data-panel alert-pink">
            <header>
              <span class="alert-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 16c1.4-1.8 2.8-2.7 4.2-2.7 2.1 0 2.6 2.7 4.7 2.7 1.5 0 3.1-1 5.1-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5 11c1.4-1.8 2.8-2.7 4.2-2.7 2.1 0 2.6 2.7 4.7 2.7 1.5 0 3.1-1 5.1-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17 6h3v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3>Steam</h3>
            </header>
            <p>
              Prices and/or lines on sportsbooks are getting more expensive as wagering is coming
              in on that side of the market.
            </p>
          </article>
        </div>
      </section>

      <section class="section-card data-panel accent-violet">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            </svg>
          </span>
          <h2>Paulie Chat</h2>
        </div>
        <div class="copy-block">
          <p>
            Paulie Chat is the internet distilled for sports betting-related data, with a chat
            engine layered on top to make that information usable.
          </p>
          <p>
            Because Paulie Chat uses LLM tooling, hallucinations can still happen at times, but
            this remains the best way we know to distill this much data into something usable.
          </p>
        </div>
      </section>

      <section class="section-card data-panel accent-gold">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.5"/>
              <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.5"/>
              <path d="M12 12 17.5 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
          <h2>Intended Usage</h2>
        </div>
        <div class="usage-grid">
          <article class="usage-card data-panel">
            <h3>Check your ideas with data</h3>
            <p>How did teams playing back-to-back games do ATS last week?</p>
          </article>
          <article class="usage-card data-panel">
            <h3>Question your bet with relevant trends</h3>
            <p>What are trends going for and against the Celtics tonight?</p>
          </article>
          <article class="usage-card data-panel">
            <h3>Start from Home alerts</h3>
            <p>Copy a prompt from an alert on the Home tab.</p>
          </article>
        </div>
      </section>

      <section class="support data-panel">
        <h2>Have an idea, question, or complaint?</h2>
        <p>We want to talk and implement your ideas.</p>
        <a href="mailto:support@paulie.chat">support@paulie.chat</a>
      </section>
    </div>
  </div>
</template>

<style scoped>
.data-page {
  min-height: 100vh;
  background: transparent;
}

.data-container {
  width: min(1100px, 100%);
  margin: 0 auto;
  padding: 1.9rem 0.9rem 3rem;
  display: grid;
  gap: 0.75rem;
}

.hero {
  padding: 1.5rem 1.15rem;
  text-align: left;
}

.hero h1 {
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 0.6rem;
  text-shadow: none;
}

.hero p {
  max-width: 780px;
  margin: 0;
  color: var(--text-muted);
}

.data-panel {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
}

.section-card {
  --section-accent-rgb: 125, 211, 252;
  padding: 1rem 1rem 1.05rem 0.95rem;
  border-left: 3px solid rgb(var(--section-accent-rgb));
}

.accent-sky { --section-accent-rgb: 91, 124, 255; }
.accent-fuchsia { --section-accent-rgb: 91, 124, 255; }
.accent-green { --section-accent-rgb: 134, 239, 172; }
.accent-violet { --section-accent-rgb: 91, 124, 255; }
.accent-gold { --section-accent-rgb: 251, 191, 36; }
.accent-neutral { --section-accent-rgb: 148, 163, 184; }

.section-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.section-icon {
  width: 0.95rem;
  height: 0.95rem;
  line-height: 1;
  color: rgb(var(--section-accent-rgb));
}

.section-icon svg,
.alert-icon svg {
  display: block;
  width: 100%;
  height: 100%;
}

.section-head h2 {
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.section-intro,
.section-subtitle {
  margin-bottom: 0.75rem;
}

.chip-row,
.schema-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.75rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.24rem 0.58rem;
  font-size: 0.8rem;
  background: #fbfbf8;
  color: var(--text-primary);
}

.copy-block {
  display: grid;
  gap: 0.65rem;
}

.copy-block strong {
  color: var(--text-primary);
}

.trend-table-wrap {
  margin-top: 0.85rem;
  overflow-x: auto;
}

.trend-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #ffffff;
  box-shadow: var(--shadow-sm);
}

.trend-table th,
.trend-table td {
  padding: 0.65rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.trend-table thead th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  background: #fbfbf8;
}

.trend-table tbody th {
  width: 34%;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.83rem;
  color: var(--text-primary);
}

.trend-table tbody td {
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.trend-table tbody tr:last-child th,
.trend-table tbody tr:last-child td {
  border-bottom: 0;
}

.sub-card {
  margin-top: 0.15rem;
  padding: 0.85rem;
}

.sub-card h3 {
  font-size: 1rem;
  margin-bottom: 0.75rem;
}

.bucket-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.bucket {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.75rem;
  background: #fbfbf8;
}

.bucket h4 {
  font-size: 0.92rem;
  margin-bottom: 0.45rem;
}

.bucket p {
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.bucket-blue h4 { color: var(--accent); }
.bucket-amber h4 { color: #f59e0b; }
.bucket-fuchsia h4 { color: #f0abfc; }

.callout-grid {
  margin-top: 0.75rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}

.callout {
  border-radius: 10px;
  padding: 0.75rem;
  border: 1px solid var(--border);
}

.callout h4 {
  font-size: 0.96rem;
  margin-bottom: 0.45rem;
}

.callout p {
  font-size: 0.85rem;
}

.callout-blue { border-left: 3px solid var(--accent); }

.callout-amber {
  border-left: 3px solid #f59e0b;
}

.intro-box {
  border: 1px solid var(--border);
  border-radius: 10px;
  border-left: 3px solid var(--text-muted);
  background: var(--bg-card);
  padding: 0.72rem;
  margin-bottom: 0.75rem;
}

.alert-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.alert-card {
  padding: 0.75rem;
}

.alert-card header {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.45rem;
}

.alert-icon {
  width: 1rem;
  height: 1rem;
  color: var(--accent);
}

.alert-card h3 {
  font-size: 1rem;
}

.alert-card p {
  font-size: 0.88rem;
  margin-bottom: 0.6rem;
}

.alert-card footer {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.alert-green {
  border-left: 3px solid #22c55e;
}

.alert-amber {
  border-left: 3px solid #f59e0b;
}

.alert-red {
  border-left: 3px solid #ef4444;
}

.alert-teal {
  border-left: 3px solid #14b8a6;
}

.alert-pink {
  border-left: 3px solid #ec4899;
}

.usage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.usage-card {
  padding: 0.8rem;
}

.usage-card h3 {
  font-size: 0.92rem;
  margin-bottom: 0.4rem;
}

.usage-card p {
  font-size: 0.87rem;
}

.support {
  text-align: center;
  padding: 1.15rem 0.95rem;
}

.support h2 {
  font-size: 1.35rem;
  margin-bottom: 0.5rem;
}

.support p {
  margin-bottom: 0.5rem;
}

.support a {
  color: var(--accent);
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 1024px) {
  .bucket-grid,
  .alert-grid,
  .usage-grid {
    grid-template-columns: 1fr 1fr;
  }

  .bucket-grid .bucket:last-child,
  .alert-grid .alert-card:last-child,
  .usage-grid .usage-card:last-child {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .data-container {
    padding: 1rem 0.8rem 2.2rem;
  }

  .hero,
  .section-card,
  .support {
    padding: 0.9rem;
  }

  .bucket-grid,
  .callout-grid,
  .alert-grid,
  .usage-grid {
    grid-template-columns: 1fr;
  }

  .bucket-grid .bucket:last-child,
  .alert-grid .alert-card:last-child,
  .usage-grid .usage-card:last-child {
    grid-column: auto;
  }
}
</style>
